import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { toggleModal } from '../Modal/modalActions';
import { dataTransform } from '@nexio/emvio-util-app';
import Table from '../Table/Table';
import GenericDropDownFilter from '../Table/Filters/GenericDropDownFilter/GenericDropDownFilter';
import BoardingStatusSummaryWidget from '../BoardingStatusSummaryWidget/BoardingStatusSummaryWidget';
import PrevetListItem from './PrevetListItem/PrevetListItem';
import MerchantOnboardingListItem from './MerchantOnboardingListItem/MerchantOnboardingListItem';
import { BOARDING_STATUSES, STATUS_TO_TABLE_SORT_MAP } from '../BoardingStatusSummaryWidget/BoardingStatusSummaryWidget';
import {
    getPrevets,
    getIsPrevetLoading,
    getPrevetErrorMessage,
    getCount,
    getPages,
    getShouldRefresh
} from './leadToLiveEntity';
import {
    fetchPrevets,
    fetchUrl,
    selectPrevet
} from './leadToLiveActions';
import { getBoardingStatusSummary as fetchBoardingStatusSummary } from '../BoardingStatusSummaryWidget/boardingStatusSummaryActions';
import {
    getMerchantOnboarding,
    getIsMerchantOnboardingLoading,
    getMerchantOnboardingErrorMessage,
    getMerchantCount,
    getMerchantPages
} from './merchantOnboardingEntity';
import {
    getMerchantOnboarding as fetchMerchantOnboarding,
    fetchMerchantUrl,
    selectMerchant
} from './merchantOnboardingActions';
import {
    getApplications as fetchApplications,
    fetchApplicationsUrl,
    selectApplication
} from './applicationActions';
import './LeadToLive.scss';
import { getApplications, getApplicationsCount, getApplicationsErrorMessage, getIsApplicationsLoading, getApplicationsPages } from './applicationEntity';
import ApplicationListItem from './ApplicationListItem/ApplicationListItem';
import { saveToSessionStorage, loadFromSessionStorage } from '../../utils/sessionStorage';
import { createPathToLiveUrlWithParams } from './utils/queryParamsUtils';

const PATH_TO_LIVE_STATE_KEY = 'pathToLiveState';

const TABS = [
    { id: 'prevet', label: 'Pre-Vet', active: true },
    { id: 'applications', label: 'Applications', active: false },
    { id: 'merchants', label: 'Merchants', active: false }
];

const decisionOutcomeMap = {
    0: 'Approval Likely',
    1: 'Approval Conditional',
    2: 'Approval Unlikely'
};

export class LeadToLive extends Component {
    static propTypes = {
        history: PropTypes.object,
        onToggleModal: PropTypes.func.isRequired,
        onGetBoardingStatusSummary: PropTypes.func.isRequired,
        shouldRefresh: PropTypes.number
    };

    constructor(props) {
        super(props);

        const searchParams = new URLSearchParams(props.location.search);
        const tab = searchParams.get('tab');
        const status = searchParams.get('status');

        // Track if we're in a manual refresh to prevent duplicate statusSummary calls
        this.isManualRefresh = false;

        let savedState = null;
        if (!tab && !status) {
            savedState = loadFromSessionStorage(PATH_TO_LIVE_STATE_KEY);
        }
        this.state = {
            activeTab: tab || savedState?.activeTab || 'prevet',
            statusFilter: status || savedState?.statusFilter || null,
            needsRefetch: false,
            tableKey: Date.now()
        };

        this.defaultColumnsAndProps = [
            {
                accessor: 'legalName',
                filterable: true,
                label: 'MERCHANT',
                minWidth: 100,
                show: true,
                sortable: true
            },
            {
                accessor: 'decisionOutcome',
                filterable: true,
                label: 'PRE-VET RESULT',
                minWidth: 100,
                show: true,
                sortable: true,
                Cell: ({ value }) => decisionOutcomeMap?.[value] ?? '',
                Filter: ({ filter, onChange }) => {
                    // Convert integer filter value to display string for dropdown
                    const displayFilter = filter && filter.value !== undefined
                        ? { ...filter, value: decisionOutcomeMap[filter.value] || filter.value }
                        : filter;

                    return (
                        <GenericDropDownFilter
                            filter={displayFilter}
                            onChange={(value) => {
                                // Map display string back to integer for filtering
                                const intValue = Object.keys(decisionOutcomeMap).find(
                                    (key) => decisionOutcomeMap[key] === value
                                );
                                onChange(intValue || value);
                            }}
                            values={Object.values(decisionOutcomeMap)}
                        />
                    );
                }
            },
            {
                accessor: 'status',
                filterable: true,
                label: 'STATUS',
                minWidth: 100,
                show: true,
                sortable: true,
                Cell: ({ value }) => 'Pre-vet',
                Filter: ({ filter, onChange }) => (
                    <GenericDropDownFilter
                        filter={filter}
                        onChange={(value) => this.handleStatusFilterChange(value, onChange)}
                        values={BOARDING_STATUSES}
                    />
                )
            },
            {
                accessor: 'statusDate',
                label: 'STATUS DATE',
                filterable: false,
                show: true,
                transformData: dataTransform.dateTimezoneButHideTimezone,
                transformType: 'dateTimezoneButHideTimezone'
            },
            {
                accessor: 'actions',
                label: 'ACTIONS',
                filterable: false,
                show: true,
                sortable: false,
                Cell: ({ row }) => (
                    <button
                        className="create-application-btn"
                        onClick={(e) => {
                            e.stopPropagation();
                            this.handleCreateApplicationFromPrevet(row);
                        }}
                    >
                        Create Application
                    </button>
                )
            }
        ];
    }

    componentDidMount() {
        this.updateTimer = setTimeout(() => {
            this.updateFromURLParams();
        }, 300);
    }

    componentDidUpdate(prevProps) {
        if (prevProps.location.search !== this.props.location.search) {
            if (this.updateTimer) {
                clearTimeout(this.updateTimer);
            }

            this.updateTimer = setTimeout(() => {
                this.updateFromURLParams();
            }, 300);
        }

        if (prevProps.shouldRefresh !== this.props.shouldRefresh) {
            this.handleRefresh();
        }

        if (prevProps.location.search !== this.props.location.search) {
            this.updateFromURLParams(true);
        }

    }

    componentWillUnmount() {
        if (this.updateTimer) {
            clearTimeout(this.updateTimer);
        }
    }

    /**
     * Saves state to session storage
     * Single Responsibility: Handles state persistence
     */
    saveStateToSessionStorage = (activeTab, statusFilter) => {
        saveToSessionStorage(PATH_TO_LIVE_STATE_KEY, {
            activeTab,
            statusFilter
        });
    };

    /**
     * Updates URL with new tab and status while preserving other query params
     * Single Responsibility: Handles URL updates
     */
    updateUrlWithTabAndStatus = (tab, status = null, useReplace = false) => {
        const newUrl = createPathToLiveUrlWithParams(
            this.props.location.search,
            tab,
            status
        );

        if (useReplace) {
            this.props.history.replace(newUrl);
        } else {
            this.props.history.push(newUrl);
        }
    };

    /**
     * Updates component state and persists to session storage
     * Single Responsibility: Handles state updates
     */
    updateStateAndPersist = (newState) => {
        this.setState({
            ...newState,
            tableKey: Date.now()
        });

        this.saveStateToSessionStorage(
            newState.activeTab,
            newState.statusFilter
        );
    };

    updateFromURLParams = (clearStatus = false) => {
        const searchParams = new URLSearchParams(this.props.location.search);
        const tab = searchParams.get('tab');
        const status = clearStatus ? null : searchParams.get('status');

        let savedState = null;
        if (!tab && !status) {
            savedState = loadFromSessionStorage(PATH_TO_LIVE_STATE_KEY);
        }

        const activeTab = tab || this.state.activeTab || savedState?.activeTab || 'prevet';
        const activeStatus = status || this.state.statusFilter || savedState?.statusFilter || null;

        if (!tab && this.props.location.pathname === '/pathToLive') {
            const newUrl = createPathToLiveUrlWithParams(
                this.props.location.search,
                activeTab,
                activeStatus
            );
            const currentUrl = `${this.props.location.pathname}${this.props.location.search}`;

            if (newUrl !== currentUrl) {
                this.props.history.replace(newUrl);

                return;
            }
        }

        const finalTab = tab || this.state.activeTab || savedState?.activeTab || 'prevet';
        const finalStatus = searchParams.has('status') ? status : (this.state.statusFilter !== null ? this.state.statusFilter : (savedState?.statusFilter || null));

        if (tab !== this.state.activeTab || status !== this.state.statusFilter) {
            this.updateStateAndPersist({
                activeTab: finalTab,
                statusFilter: finalStatus
            });
        }
    };

    handleTabClick = (tabId) => {
        this.updateStateAndPersist({
            activeTab: tabId,
            statusFilter: null
        });

        this.updateUrlWithTabAndStatus(tabId, null);
    };

    handleNewPreVet = () => {
        this.props.onToggleModal('prevetForm');
    };

    // Create new applicaiton from prevet table
    handleCreateApplicationFromPrevet = (prevetRow) => {
        const prevetData = prevetRow?._original || prevetRow?.original || prevetRow;

        const prevetId = prevetData?.id;

        if (!prevetId) {
            return;
        }

        this.props.onToggleModal('createApplicationModal', {
            isNewApplication: false,
            prevetId: prevetId
        });
    };

    // Create new applicaiton from applicaiton table
    handleCreateNewApplication = () => {
        this.props.onToggleModal('createApplicationModal', {
            isNewApplication: true
        });
    };

    handleRefresh = () => {
        //this refreshes the boarding status summary widget
        this.props.onGetBoardingStatusSummary();

        // Mark as manual refresh to prevent duplicate call in handleTableDataFetched
        this.isManualRefresh = true;

        //this refreshes the table
        this.setState({ needsRefetch: true });
    };

    handleResetNeedsRefetch = () => {
        this.setState({ needsRefetch: false });
    };

    handleTableDataFetched = () => {
        // If this was triggered by a manual refresh button click, skip the duplicate call
        // (handleRefresh already called onGetBoardingStatusSummary)
        if (this.isManualRefresh) {
            this.isManualRefresh = false;

            return;
        }

        // Silently refresh the summary widget when table data changes (filters, sorts, etc.)
        // This updates the counts in the background without showing loading state
        this.props.onGetBoardingStatusSummary(true); // true = silent mode
    };

    handleStatusFilterChange = (statusValue, tableOnChange) => {
        const targetTab = STATUS_TO_TABLE_SORT_MAP[statusValue] || this.state.activeTab;
        const newTab = statusValue ? targetTab : this.state.activeTab;

        this.updateStateAndPersist({
            activeTab: newTab,
            statusFilter: statusValue || null
        });

        this.updateUrlWithTabAndStatus(newTab, statusValue || null);

        if (tableOnChange) {
            tableOnChange(statusValue);
        }
    };

    getTableState = (tableState) => {
        this.setState({
            ...tableState
        });
    };

    getFilteredFetchUrl = () => {
        const { statusFilter } = this.state;

        if (!statusFilter) {
            return fetchUrl;
        }

        const separator = fetchUrl.includes('?') ? '&' : '?';

        return `${fetchUrl}${separator}status=${encodeURIComponent(statusFilter)}`;
    };

    getDefaultFiltered = () => {
        const { statusFilter } = this.state;

        if (statusFilter) {
            return [
                {
                    id: 'status',
                    value: statusFilter
                }
            ];
        }

        return [];
    };

    getMerchantColumns = () => {
        return [
            {
                label: 'MERCHANT',
                accessor: 'legalName',
                filterable: true,
                show: true,
                sortable: true
            },
            {
                label: 'MERCHANT ID',
                accessor: 'merchantId',
                filterable: true,
                show: true,
                sortable: true
            },
            {
                label: 'STATUS',
                accessor: 'status',
                filterable: true,
                show: true,
                sortable: true,
                Filter: ({ filter, onChange }) => (
                    <GenericDropDownFilter
                        filter={filter}
                        onChange={(value) => this.handleStatusFilterChange(value, onChange)}
                        values={BOARDING_STATUSES}
                    />
                )
            },
            {
                label: 'STATUS DATE',
                accessor: 'statusDate',
                filterable: false,
                show: true,
                sortable: true,
                transformData: dataTransform.dateTimezoneButHideTimezone,
                transformType: 'dateTimezoneButHideTimezone'
            },
            {
                label: 'SIGNER',
                accessor: 'signer',
                filterable: true,
                show: true,
                sortable: true
            },
            {
                label: 'PHONE',
                accessor: 'phone',
                filterable: true,
                show: true,
                sortable: true
            },
            {
                label: 'APP ID',
                accessor: 'appId',
                filterable: true,
                show: true,
                sortable: true
            }
        ];
    };

    getApplicationsColumns = () => {
        return [
            {
                label: 'MERCHANT',
                accessor: 'legalName',
                filterable: true,
                show: true,
                sortable: true
            },
            {
                label: 'STATUS',
                accessor: 'status',
                filterable: true,
                show: true,
                sortable: true,
                Filter: ({ filter, onChange }) => (
                    <GenericDropDownFilter
                        filter={filter}
                        onChange={(value) => this.handleStatusFilterChange(value, onChange)}
                        values={BOARDING_STATUSES}
                    />
                )
            },
            {
                label: 'STATUS DATE',
                accessor: 'statusDate',
                filterable: false,
                show: true,
                sortable: true,
                transformData: dataTransform.dateTimezoneButHideTimezone,
                transformType: 'dateTimezoneButHideTimezone'
            },
            {
                label: 'CONTACT',
                accessor: 'contactName',
                filterable: true,
                show: true,
                sortable: true
            },
            {
                label: 'CONTACT EMAIL',
                accessor: 'contactEmail',
                filterable: true,
                show: true,
                sortable: true
            },
            {
                label: 'PRICING',
                accessor: 'pricingType',
                filterable: true,
                show: true,
                sortable: true
            }
        ];
    };

    renderTabs() {
        return (
            <div className="tab-container">
                <div className="tab-headers">
                    {TABS.map((tab) => (
                        <div
                            key={tab.id}
                            className={`tab-header ${this.state.activeTab === tab.id ? 'active' : ''}`}
                            onClick={() => this.handleTabClick(tab.id)}
                        >
                            {tab.label}
                        </div>
                    ))}
                </div>
                <div className="tab-content">
                    {this.renderTabContent()}
                </div>
            </div>
        );
    }

    renderTabContent() {
        switch (this.state.activeTab) {
            case 'prevet':
                return this.renderPreVetTable();
            case 'applications':
                return this.renderApplicationsTable();
            case 'merchants':
                return this.renderMerchantsTable();
            default:
                return null;
        }
    }

    renderApplicationsTable() {
        return (
            <div className="applications-table-container">
                <div className="table-header">
                    <button
                        className="new-application-btn"
                        onClick={this.handleCreateNewApplication}
                    >
                        New Application
                    </button>
                </div>

                <Table
                    key={this.state.tableKey}
                    fetchUrl={fetchApplicationsUrl}
                    className="emvio-applications-table"
                    shouldHideDateRange={true}
                    defaultColumnsAndProps={this.getApplicationsColumns()}
                    defaultFiltered={this.getDefaultFiltered()}
                    defaultSorted={[{
                        desc: true,
                        id: 'statusDate'
                    }]}
                    fetchData={fetchApplications}
                    countSelector={getApplicationsCount}
                    dataSelector={getApplications}
                    errorMessageSelector={getApplicationsErrorMessage}
                    isDataLoadingSelector={getIsApplicationsLoading}
                    pagesSelector={getApplicationsPages}
                    itemKey="appId"
                    hasNoCount={false}
                    ListItemComponent={ApplicationListItem}
                    noDataText="No Applications Found"
                    uniqueTableName="Applications"
                    getTableState={this.getTableState}
                    filterable={true}
                    shouldUseProvidedMerchantName={true}
                    selectRow={selectApplication}
                    needsRefetch={this.state.needsRefetch}
                    resetNeedsRefetch={this.handleResetNeedsRefetch}
                    onDataFetched={this.handleTableDataFetched}
                />
            </div>
        );
    }

    renderMerchantsTable() {
        return (
            <div className="merchants-table-container">
                <Table
                    key={this.state.tableKey}
                    fetchUrl={fetchMerchantUrl}
                    className="emvio-merchants-table"
                    shouldHideDateRange={true}
                    defaultColumnsAndProps={this.getMerchantColumns()}
                    defaultFiltered={this.getDefaultFiltered()}
                    defaultSorted={[{
                        desc: true,
                        id: 'statusDate'
                    }]}
                    fetchData={fetchMerchantOnboarding}
                    countSelector={getMerchantCount}
                    dataSelector={getMerchantOnboarding}
                    errorMessageSelector={getMerchantOnboardingErrorMessage}
                    isDataLoadingSelector={getIsMerchantOnboardingLoading}
                    pagesSelector={getMerchantPages}
                    itemKey="merchantId"
                    hasNoCount={false}
                    ListItemComponent={MerchantOnboardingListItem}
                    noDataText="No Merchants Found"
                    uniqueTableName="Merchants"
                    getTableState={this.getTableState}
                    filterable={true}
                    shouldUseProvidedMerchantName={true}
                    selectRow={selectMerchant}
                    needsRefetch={this.state.needsRefetch}
                    resetNeedsRefetch={this.handleResetNeedsRefetch}
                    onDataFetched={this.handleTableDataFetched}
                />
            </div>
        );
    }

    renderPreVetTable() {
        return (
            <div className="prevet-table-container">
                <div className="table-header">
                    <button
                        className="new-prevet-btn"
                        onClick={this.handleNewPreVet}
                    >
                        New Pre-Vet
                    </button>
                </div>
                <Table
                    key={this.state.tableKey}
                    fetchUrl={this.getFilteredFetchUrl()}
                    className="emvio-prevet-table"
                    shouldHideDateRange={true}
                    defaultColumnsAndProps={this.defaultColumnsAndProps}
                    defaultFiltered={this.getDefaultFiltered()}
                    defaultSorted={[{
                        desc: true,
                        id: 'statusDate'
                    }]}
                    fetchData={fetchPrevets}
                    countSelector={getCount}
                    dataSelector={getPrevets}
                    errorMessageSelector={getPrevetErrorMessage}
                    isDataLoadingSelector={getIsPrevetLoading}
                    pagesSelector={getPages}
                    itemKey="id"
                    hasNoCount={false}
                    ListItemComponent={PrevetListItem}
                    noDataText="No Pre-Vet Records Found"
                    uniqueTableName="LeadToLive"
                    getTableState={this.getTableState}
                    filterable={true}
                    selectRow={selectPrevet}
                    columnsToNotClick={['actions']}
                    needsRefetch={this.state.needsRefetch}
                    resetNeedsRefetch={this.handleResetNeedsRefetch}
                    onDataFetched={this.handleTableDataFetched}
                />
            </div>
        );
    }

    render() {
        return (
            <div className="emvio-lead-to-live">
                <BoardingStatusSummaryWidget />
                {this.renderTabs()}
            </div>
        );
    }
}

function mapStateToProps(state) {
    return {
        shouldRefresh: getShouldRefresh(state)
    };
}

function mapDispatchToProps(dispatch) {
    return {
        onToggleModal: (modalName, data) => dispatch(toggleModal(modalName, data)),
        onGetBoardingStatusSummary: (silent = false) => dispatch(fetchBoardingStatusSummary(silent))
    };
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(LeadToLive));
